/**
 * AssetTypeCell
 *
 * Renders a small badge labelling each row's asset kind ('Application',
 * 'Web Application', 'IP', 'Domain', or the row's prettified `class` for
 * anything else — e.g. `AWS::IAM::Group` renders as "IAM Group"). Derived
 * via the shared `assetTypeLabel()` util so this is consistent with the
 * drawer header and any future labelling. The raw class is preserved in
 * the badge's title attribute since prettifying is lossy.
 *
 * Uses the shadcn `Badge` primitive — brand-token classes only, no hex.
 * Applications get the `default` (primary/crimson) variant; everything
 * else gets `secondary` so the rollup root stands out at a glance.
 *
 * See rollup-frontend-plan.md item C.
 */
import React from 'react';

import { Badge } from '@/components/ui/badge';
import { assetType, assetTypeLabel } from '@/sections/asset/utils';
import type { Asset } from '@/types';

interface AssetTypeCellProps {
  asset: Asset;
}

export const AssetTypeCell: React.FC<AssetTypeCellProps> = ({ asset }) => {
  const kind = assetType(asset);
  const variant = kind === 'application' ? 'default' : 'secondary';

  return (
    <Badge variant={variant} className="font-normal" title={asset.class}>
      {assetTypeLabel(asset)}
    </Badge>
  );
};
